import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Button, CircularProgress, TextField } from '@mui/material';
import { DashboardContainer } from '../../Containers/DashboardLayout';
import { Toasts } from '../../Components/Toasts/Toasts';
import {
  clearCursorCache,
  fetchPhoneVerifications,
  getCountOfPendingVerifications,
  verifyPhoneVerification,
} from './saga/saga';

const PAGE_SIZE = 25;

export const PhoneVerifications = () => {
  const [page, setPage] = useState(0);
  const [list, setList] = useState([]);
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  // Local state per-row: the phone number the admin is typing + busy flag.
  const [phoneByRow, setPhoneByRow] = useState({});
  const [busyByRow, setBusyByRow] = useState({});

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const items = await fetchPhoneVerifications({ page: p, docLimit: PAGE_SIZE });
      setList((prev) => (p === 0 ? items : [...prev, ...items]));
      if (items.length < PAGE_SIZE) setExhausted(true);
    } catch (e) {
      Toasts.error(e?.response?.data?.message || e?.message || 'failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(0);
    getCountOfPendingVerifications().then(setCount).catch(() => null);
    return () => clearCursorCache();
  }, [load]);

  const onLoadMore = () => {
    const next = page + 1;
    setPage(next);
    load(next);
  };

  const onVerify = async (id) => {
    const phone = (phoneByRow[id] || '').trim();
    if (!phone) {
      Toasts.error('Enter the phone number that sent the message.');
      return;
    }
    setBusyByRow((prev) => ({ ...prev, [id]: true }));
    try {
      await verifyPhoneVerification({ id, phone });
      // Drop the row from the list — it's no longer pending.
      setList((prev) => prev.filter((r) => r.id !== id));
      setCount((c) => (c == null ? c : Math.max(0, c - 1)));
      Toasts.success('Verified. The user will be signed in on their next poll.');
    } catch (e) {
      Toasts.error(e?.response?.data?.message || e?.message || 'verify failed');
    } finally {
      setBusyByRow((prev) => ({ ...prev, [id]: false }));
    }
  };

  return (
    <DashboardContainer>
      <div className="p-4">
        <h3 className="text-[22px] font-bold">
          Phone Verifications {count !== null && count > 0 && ` - ${count} pending`}
        </h3>
        <p className="text-[13px] text-gray-500 mb-3">
          Inbound verification: a user is waiting for you to confirm the code
          they sent via SMS or WhatsApp. Find the matching code in your
          messages, type the source phone number here, and click Verify. The
          user will be signed in within ~3 seconds.
        </p>

        <div className="mt-2 bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-left text-[14px]" style={{ minWidth: 760 }}>
            <thead className="bg-gray-50 text-gray-600 text-[12px] uppercase">
              <tr>
                <th className="px-3 py-2 whitespace-nowrap">code</th>
                <th className="px-3 py-2 whitespace-nowrap">requested</th>
                <th className="px-3 py-2 whitespace-nowrap">anon id</th>
                <th className="px-3 py-2">phone (from inbound message)</th>
                <th className="px-3 py-2 whitespace-nowrap">action</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && !loading ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                    No pending verifications.
                  </td>
                </tr>
              ) : (
                list.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="font-mono font-bold text-[16px] tracking-wider">
                        {row.nonce}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                      {moment(row.createdAt).fromNow()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-[12px] text-gray-500">
                      {row.originatingUserId ? `${row.originatingUserId.slice(0, 8)}…` : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <TextField
                        size="small"
                        placeholder="+1 555 0100"
                        value={phoneByRow[row.id] || ''}
                        onChange={(e) =>
                          setPhoneByRow((prev) => ({ ...prev, [row.id]: e.target.value }))
                        }
                        onKeyDown={(e) => e.key === 'Enter' && onVerify(row.id)}
                        disabled={!!busyByRow[row.id]}
                        sx={{ width: 220 }}
                      />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onVerify(row.id)}
                        disabled={!!busyByRow[row.id]}
                      >
                        {busyByRow[row.id] ? <CircularProgress size={16} /> : 'Verify'}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-center mt-4">
          {loading ? (
            <CircularProgress size={20} />
          ) : exhausted ? (
            <span className="text-gray-400 text-[13px]">No more rows.</span>
          ) : (
            <Button onClick={onLoadMore} variant="outlined" size="small">
              Load more
            </Button>
          )}
        </div>
      </div>
    </DashboardContainer>
  );
};
